import React, { useState, useEffect } from "react";
import { api, LibrarySlide, DeckBrief, ProjectSlide } from "../api/client";

const F = "'Roboto Mono', monospace";

interface SlideBrowserProps {
  deckBrief: DeckBrief | null;
  sessionId: string | null;
  selectedSlides: ProjectSlide[];
  onSlideSelect: (slide: ProjectSlide) => void;
}

export const SlideBrowser: React.FC<SlideBrowserProps> = ({
  deckBrief,
  sessionId,
  selectedSlides,
  onSlideSelect,
}) => {
  const [allSlides, setAllSlides] = useState<LibrarySlide[]>([]);
  const [isLoadingSlides, setIsLoadingSlides] = useState(true);
  const [filter, setFilter] = useState("");
  const [fileFilter, setFileFilter] = useState("");

  // Load all slides on mount
  useEffect(() => {
    setIsLoadingSlides(true);
    api.listAllSlides()
      .then((data) => setAllSlides(data.slides))
      .catch(() => {})
      .finally(() => setIsLoadingSlides(false));
  }, []);

  const isSelected = (fileId: string, slideNumber: number) =>
    selectedSlides.some(
      (s) => s.file_id === fileId && s.slide_number === slideNumber
    );

  const handleSelect = (fileId: string, fileName: string, slideNumber: number) => {
    onSlideSelect({
      file_id: fileId,
      file_name: fileName,
      slide_number: slideNumber,
      title: `${fileName} — Slide ${slideNumber}`,
      file_type: "pptx",
      thumbnail: null,
    });
  };

  // Get unique file names for the file filter
  const fileNames = Array.from(new Set(allSlides.map((s) => s.file_name))).sort();

  // Filter slides
  const filteredSlides = allSlides.filter((s) => {
    if (fileFilter && s.file_name !== fileFilter) return false;
    if (filter) {
      const q = filter.toLowerCase();
      return s.file_name.toLowerCase().includes(q);
    }
    return true;
  });

  return (
    <div style={styles.container}>
      {/* Slide library */}
      <div style={styles.browseSection}>
        <div style={styles.sectionLabel}>SLIDE LIBRARY</div>
        <div style={styles.filterRow}>
          <select
            value={fileFilter}
            onChange={(e) => setFileFilter(e.target.value)}
            style={styles.fileSelect}
          >
            <option value="">All files</option>
            {fileNames.map((name) => (
              <option key={name} value={name}>{name}</option>
            ))}
          </select>
          <input
            type="text"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            placeholder="Search..."
            style={styles.searchInput}
          />
          <span style={styles.slideCount}>
            {filteredSlides.length} slide{filteredSlides.length !== 1 ? "s" : ""}
          </span>
        </div>

        {isLoadingSlides ? (
          <div style={styles.loadingState}>Loading slide library...</div>
        ) : (
          <div style={styles.thumbGrid}>
            {filteredSlides.map((s) => {
              const selected = isSelected(s.file_id, s.slide_number);
              return (
                <div
                  key={`${s.file_id}-${s.slide_number}`}
                  style={{
                    ...styles.thumbCard,
                    ...(selected ? styles.thumbCardSelected : {}),
                  }}
                  onClick={() => handleSelect(s.file_id, s.file_name, s.slide_number)}
                >
                  <img
                    src={api.getThumbnailUrl(s.file_id, s.slide_number)}
                    alt={`Slide ${s.slide_number}`}
                    style={styles.thumbImg}
                    loading="lazy"
                  />
                  <div style={styles.thumbCaption}>
                    <div style={styles.thumbFile}>{s.file_name}</div>
                    <div style={styles.thumbMeta}>Slide {s.slide_number}</div>
                  </div>
                  {selected && <div style={styles.selectedBadge}>SELECTED</div>}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  container: {
    display: "flex",
    flexDirection: "column",
    height: "100%",
    overflow: "hidden",
  },
  recSection: {
    flexShrink: 0,
    padding: "12px 16px",
    borderBottom: "2px solid #E0E0E0",
    maxHeight: "40%",
    overflow: "auto",
  },
  recHeader: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    marginBottom: 8,
  },
  sectionLabel: {
    fontSize: 11,
    fontWeight: 700,
    color: "#666",
    letterSpacing: 0.5,
    marginBottom: 8,
    textTransform: "uppercase" as const,
    paddingBottom: 10,
    borderBottom: "1px solid #E8E8E8",
  },
  loading: {
    fontSize: 9,
    color: "#888",
  },
  browseSection: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    padding: "12px 16px",
    overflow: "hidden",
  },
  filterRow: {
    display: "flex",
    gap: 8,
    alignItems: "center",
    marginBottom: 10,
    flexShrink: 0,
  },
  fileSelect: {
    padding: "6px 8px",
    border: "1px solid #E0E0E0",
    borderRadius: 4,
    fontSize: 10,
    fontFamily: F,
    background: "#fff",
    flex: 1,
  },
  searchInput: {
    padding: "6px 8px",
    border: "1px solid #E0E0E0",
    borderRadius: 4,
    fontSize: 10,
    fontFamily: F,
    width: 120,
  },
  slideCount: {
    fontSize: 9,
    fontWeight: 700,
    color: "#666",
    letterSpacing: 0.5,
    flexShrink: 0,
  },
  thumbGrid: {
    display: "flex",
    flexWrap: "wrap" as const,
    gap: 8,
    overflowY: "auto",
    flex: 1,
  },
  thumbCard: {
    width: 140,
    border: "1px solid #E0E0E0",
    borderRadius: 6,
    background: "#fff",
    cursor: "pointer",
    position: "relative" as const,
    boxShadow: "0 1px 4px rgba(0,0,0,0.04)",
  },
  thumbCardSelected: {
    border: "2px solid #FF0000",
  },
  thumbImg: {
    width: "100%",
    height: 85,
    objectFit: "cover" as const,
    display: "block",
    background: "#F5F5F5",
    borderRadius: "6px 6px 0 0",
  },
  thumbPlaceholder: {
    width: "100%",
    height: 85,
    background: "#F5F5F5",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 16,
    fontWeight: 700,
    color: "#888",
    borderRadius: "6px 6px 0 0",
  },
  thumbCaption: {
    padding: "4px 6px",
  },
  thumbFile: {
    fontSize: 8,
    fontWeight: 700,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap" as const,
    color: "#1a1a1a",
  },
  thumbMeta: {
    fontSize: 7,
    color: "#888",
  },
  selectedBadge: {
    position: "absolute" as const,
    top: 4,
    right: 4,
    background: "#FF0000",
    color: "#fff",
    fontSize: 7,
    fontWeight: 700,
    padding: "1px 4px",
    letterSpacing: 0.3,
    borderRadius: 3,
  },
  loadingState: {
    textAlign: "center" as const,
    padding: 40,
    color: "#666",
    fontSize: 11,
  },
};
